import React, { useState, useEffect, useRef } from 'react';
import PointDialogModal from './PointDialogModal';
import { ModelTagRow } from './modelInfo';
import { backdropClose } from '../backdrop';

// A point counts as "approved" if the critic approved it (AI-approved) OR a
// human blessed it (Good / Acceptable) — matches the ✅/tag chips in the list.
const APPROVED_TAGS = ['AI-approved', 'Good', 'Acceptable'];
const isApproved = (l) => (l.tags || []).some((t) => APPROVED_TAGS.includes(t));
const isEmpty = (l) => !((l.description || '').trim());

// Batch generation panel for a pipeline-draft quest: one control to write ALL
// (or a chosen subset of) points, running SEQUENTIALLY server-side (the critic
// of each point needs the finished text of every prior point). Live progress —
// overall bar, ETA and the current stage of each point — streams over the run's
// existing SSE channel (`pipeline_batch` events) and is restored from
// run.state.batch on reload.

async function papi(path, body) {
  const res = await fetch(`/api/pipeline${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

// ms -> "~3m 20s" / "~45s" / "done".
function fmtEta(ms) {
  if (ms == null) return '…';
  if (ms <= 0) return '0s';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

const STATUS_CHIP = {
  queued: { bg: '#9aa0a6', label: 'queued' },
  running: { bg: '#1a73e8', label: 'running' },
  approved: { bg: '#188038', label: '✓ approved' },
  max_rounds: { bg: '#9a6700', label: 'max rounds' },
  error: { bg: '#c5221f', label: 'error' },
  stopped: { bg: '#5f6368', label: 'stopped' },
  interrupted: { bg: '#5f6368', label: 'interrupted' },
};

// Statuses that didn't reach a final text — offered as "run unfinished".
const UNFINISHED = ['queued', 'running', 'interrupted', 'error'];

function PointRow({ bp, isCurrent, onOpen }) {
  const chip = STATUS_CHIP[bp.status] || STATUS_CHIP.queued;
  // A dialogue exists once a point has started (running or finished).
  const hasDialog = bp.status !== 'queued';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 4,
      background: isCurrent ? 'rgba(26,115,232,0.10)' : 'transparent', fontSize: 12,
    }}>
      <span style={{
        background: chip.bg, color: '#fff', borderRadius: 10, padding: '1px 8px',
        fontSize: 10, textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>{chip.label}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bp.name}</span>
      {bp.status === 'running' && bp.stage && (
        <span style={{ color: '#1a73e8', whiteSpace: 'nowrap' }}>{bp.stage}</span>
      )}
      {bp.status === 'error' && bp.errors && bp.errors.length > 0 && (
        // Tooltip = the primary message only; the attached diagnostic block is
        // multi-line and belongs in the point's dialogue, not in a title attr.
        <span
          title={`${bp.errors[0]}${bp.errors.length > 1 ? '\n\n(open the dialogue for the full failure details)' : ''}`}
          style={{ color: '#c5221f', whiteSpace: 'nowrap' }}
        >⚠</span>
      )}
      {hasDialog && (
        <button
          onClick={() => onOpen(bp)}
          title="Open the Writer ↔ Critic dialogue (live while running)"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13 }}
        >💬</button>
      )}
    </div>
  );
}

const PipelineBatchPanel = ({ questId, runId, locations, pointsByUuid, initialBatch, onChange, embedded, onStatus }) => {
  const [batch, setBatch] = useState(initialBatch || null);
  const [active, setActive] = useState(!!(initialBatch && (initialBatch.status === 'running' || initialBatch.status === 'waiting_limit')));
  const [showScope, setShowScope] = useState(false);
  const [selected, setSelected] = useState({}); // uuid -> bool; the exact set to run
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { uuid, name } — open Writer↔Critic
  const [collapsed, setCollapsed] = useState(false);
  // Once the user deletes the batch record, the restore effect below must not
  // re-adopt the (stale) initialBatch prop and resurrect the cleared panel.
  const clearedRef = useRef(false);

  // Restore progress after a remount / page reload: adopt the persisted batch
  // (from run.state.batch) and re-subscribe if it was still running. Guarded so
  // it never clobbers the live SSE-driven state.
  useEffect(() => {
    if (initialBatch && !batch && !clearedRef.current) {
      setBatch(initialBatch);
      if (initialBatch.status === 'running' || initialBatch.status === 'waiting_limit') setActive(true);
    }
  }, [initialBatch, batch]);

  // Only pipeline points (those present in the run's gen-meta), in list order.
  const points = (locations || []).filter((l) => l.uuid && pointsByUuid && pointsByUuid[l.uuid]);
  const emptyUuids = points.filter(isEmpty).map((l) => l.uuid);
  const notApprovedUuids = points.filter((l) => !isApproved(l)).map((l) => l.uuid);
  const allUuids = points.map((l) => l.uuid);
  const emptyCount = emptyUuids.length;

  const selectedUuids = points.filter((l) => selected[l.uuid]).map((l) => l.uuid);
  const selectedCount = selectedUuids.length;
  // Which quick-select preset the current tick set matches (highlight it); once
  // the user deviates it's implicitly custom — no separate Custom option needed.
  const sameSet = (arr) => arr.length === selectedCount && arr.every((u) => selected[u]);
  const activePreset = sameSet(emptyUuids) ? 'empty'
    : sameSet(notApprovedUuids) ? 'notapproved'
    : sameSet(allUuids) ? 'all' : 'custom';
  const setPreset = (uuids) => setSelected(Object.fromEntries(uuids.map((u) => [u, true])));

  // Live SSE while a batch is running (or was, per initialBatch). Refresh the
  // quest each time a point finishes so its freshly written text shows up.
  useEffect(() => {
    if (!active || !runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'pipeline_batch') {
        setBatch(msg.batch);
      } else if (msg.type === 'pipeline_batch_done') {
        setBatch(msg.batch);
        setActive(false);
      }
      // Note: written text + tags land in the editor live via the server's
      // per-field `location_field_updated` events — we intentionally do NOT
      // trigger a quest refetch here, which would reload/flash the whole list.
    };
    es.onerror = () => { /* auto-reconnects; ignore */ };
    return () => es.close();
  }, [active, runId]);

  const openScope = () => {
    setError(null);
    setPreset(emptyUuids); // default quick-select: the empty points
    setShowScope(true);
  };

  const start = async (body) => {
    setError(null); setBusy(true);
    try {
      const r = await papi(`/quest/${questId}/batch`, body);
      clearedRef.current = false; // a fresh batch may be restore-adopted again
      setBatch({ status: 'running', total: r.total, done_count: 0, approved_count: 0, error_count: 0, points: [], eta_ms: null, current_uuid: null });
      setActive(true);
      setShowScope(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const startBatch = () => {
    if (!selectedUuids.length) { setError('Select at least one point'); return; }
    // Always send the explicit tick set — presets are just client-side helpers.
    start({ scope: 'selected', uuids: selectedUuids });
  };

  const stopBatch = async () => {
    setError(null);
    try { await papi(`/quest/${questId}/batch/stop`, {}); } catch (e) { setError(e.message); }
  };

  const runUnfinished = () => {
    const uuids = (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).map((bp) => bp.uuid);
    if (!uuids.length) return;
    start({ scope: 'selected', uuids });
  };

  // Delete the persisted batch record (server + panel). Point texts are kept —
  // this only drops the progress/status card of an unfinished or old batch.
  const clearBatch = async () => {
    if (!window.confirm('Delete this batch record? Generated point texts are kept.')) return;
    setError(null); setBusy(true);
    try {
      await papi(`/quest/${questId}/batch/clear`, {});
      clearedRef.current = true;
      setBatch(null);
      setActive(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // A batch parked on a usage-limit wait is still live (Stop works, no resume
  // button, SSE stays subscribed) — treat it as running, but flag `paused` so
  // the header shows the retry countdown instead of an ETA.
  const paused = !!(active && batch && batch.status === 'waiting_limit');
  const running = active && batch && (batch.status === 'running' || batch.status === 'waiting_limit');
  const done = batch && batch.done_count != null ? batch.done_count : 0;
  const total = batch && batch.total != null ? batch.total : 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const unfinishedCount = batch ? (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).length : 0;

  // Report status to the tab shell (badge + running dot) when embedded.
  useEffect(() => {
    if (onStatus) onStatus({ running, done, total, status: batch ? batch.status : null, hasBatch: !!batch });
  }, [running, done, total, batch, onStatus]);

  // In the shared tabbed shell the outer frame/collapse belong to the shell.
  const effCollapsed = embedded ? false : collapsed;
  const box = embedded
    ? { padding: '4px 2px 2px' }
    : {
        border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: '10px 12px',
        margin: '8px 8px 0', background: 'rgba(26,115,232,0.04)',
      };
  const primaryBtn = { background: '#1a73e8', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const stopBtn = { background: '#c5221f', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' };

  return (
    <div style={box}>
      {/* The whole header toggles collapse (standalone only); inner buttons stopPropagation. */}
      <div
        onClick={embedded ? undefined : () => setCollapsed((c) => !c)}
        title={embedded ? undefined : (collapsed ? 'Expand' : 'Collapse')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: embedded ? 'default' : 'pointer' }}
      >
        {!embedded && <span style={{ fontWeight: 700, color: '#1a73e8' }}>⚡ Batch</span>}
        {!embedded && collapsed && batch && (
          <span style={{ fontSize: 12, color: '#5f6368' }}>
            {batch.done_count || 0}/{batch.total || 0}
            {!running && batch.status ? ` · ${batch.status}${batch.halted_on ? ` (halted at «${batch.halted_on}» — rerun to continue)` : ''}` : ''}
          </span>
        )}
        {!running && (
          <button style={primaryBtn} disabled={busy || !points.length} onClick={(e) => { e.stopPropagation(); openScope(); }}>
            Generate points…{emptyCount ? ` (${emptyCount} empty)` : ''}
          </button>
        )}
        {running && <button style={stopBtn} onClick={(e) => { e.stopPropagation(); stopBatch(); }}>■ Stop after current</button>}
        {!running && batch && unfinishedCount > 0 && (
          <button onClick={(e) => { e.stopPropagation(); runUnfinished(); }} disabled={busy} style={{ padding: '6px 12px', borderRadius: 4 }}>
            ↻ Run unfinished ({unfinishedCount})
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {paused && (
            <span style={{ fontSize: 12, color: '#9a6700', fontWeight: 600 }}>
              ⏸ usage limit — retry{batch.resume_at ? ` ~${new Date(batch.resume_at).toLocaleTimeString()}` : ''}
            </span>
          )}
          {running && !paused && <span style={{ fontSize: 12, color: '#5f6368' }}>ETA {fmtEta(batch.eta_ms)}</span>}
          {!running && batch && (
            <button
              className="remove-location-button"
              onClick={(e) => { e.stopPropagation(); clearBatch(); }}
              disabled={busy}
              title="Delete this batch record — generated point texts are kept"
              aria-label="Delete batch record"
            >×</button>
          )}
          {!embedded && <span style={{ color: '#1a73e8', fontSize: 13 }}>{collapsed ? '▸' : '▾'}</span>}
        </span>
      </div>

      {error && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {!effCollapsed && batch && (batch.points || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          {/* Overall progress bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
            <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: running ? '#1a73e8' : '#188038', transition: 'width .3s' }} />
            </div>
            <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>
              {done} / {total}
              {batch.error_count ? ` · ${batch.error_count} failed` : ''}
              {!running && batch.status ? ` · ${batch.status}${batch.halted_on ? ` (halted at «${batch.halted_on}» — rerun to continue)` : ''}` : ''}
            </span>
          </div>
          {/* Per-point rows with the current stage */}
          <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 4 }}>
            {(batch.points || []).map((bp) => (
              <PointRow key={bp.uuid} bp={bp} isCurrent={bp.uuid === batch.current_uuid} onOpen={(p) => setDialog({ uuid: p.uuid, name: p.name })} />
            ))}
          </div>
        </div>
      )}

      {showScope && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} {...backdropClose(() => setShowScope(false))}>
          <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 520, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Generate points</h3>
            <div style={{ marginBottom: 10 }}>
              <ModelTagRow label="Runs on:" keys={['tres', 'tgen', 'tcheck', 'tfact', 'writer', 'critic', 'factcheck', 'formal']} />
            </div>
            {/* Quick-select presets — they just tick the checkboxes below. Adjust
                any tick afterwards and it becomes a custom set automatically. */}
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Quick select:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {[
                { key: 'empty', label: `Only empty (${emptyUuids.length})`, uuids: emptyUuids },
                { key: 'notapproved', label: `Not approved (${notApprovedUuids.length})`, uuids: notApprovedUuids },
                { key: 'all', label: `All (${allUuids.length})`, uuids: allUuids },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.uuids)}
                  style={{
                    padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12,
                    border: activePreset === p.key ? '2px solid #1a73e8' : '1px solid var(--border,#ccc)',
                    background: activePreset === p.key ? 'rgba(26,115,232,0.10)' : 'transparent',
                    fontWeight: activePreset === p.key ? 600 : 400,
                  }}
                >{p.label}</button>
              ))}
              <button
                onClick={() => setSelected({})}
                style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent', marginLeft: 'auto' }}
              >Clear</button>
            </div>

            {/* Explicit list of exactly what will run. */}
            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
              {points.map((l) => {
                const empty = isEmpty(l);
                const approved = isApproved(l);
                return (
                  <label key={l.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 2px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!selected[l.uuid]} onChange={(e) => setSelected((s) => ({ ...s, [l.uuid]: e.target.checked }))} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                    {approved && <span style={{ fontSize: 10, color: '#188038', whiteSpace: 'nowrap' }}>✅ approved</span>}
                    {empty
                      ? <span style={{ fontSize: 10, opacity: 0.5 }}>empty</span>
                      : <span style={{ fontSize: 10, color: approved ? '#188038' : '#9a6700' }}>written</span>}
                  </label>
                );
              })}
            </div>

            <div style={{ fontSize: 12, marginTop: 8 }}>
              <strong>{selectedCount}</strong> point(s) will be generated
              {activePreset !== 'custom' ? '' : ' (custom)'}
            </div>
            <div style={{ fontSize: 12, color: '#9a6700', background: 'rgba(154,103,0,0.08)', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
              Sequential critic loop, up to {(batch && batch.max_rounds) || 10} rounds each. This can take a while and costs tokens.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowScope(false)}>Cancel</button>
              <button style={primaryBtn} disabled={busy || selectedCount === 0} onClick={startBatch}>Start ({selectedCount})</button>
            </div>
          </div>
        </div>
      )}

      {dialog && (
        <PointDialogModal
          questId={questId}
          uuid={dialog.uuid}
          pointName={dialog.name}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};

export default PipelineBatchPanel;
